'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useIntl } from 'react-intl';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useState } from 'react';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { ratingCardMessages } from './RatingCard.messages';

export type RatingCardActionsMenuProps = {
  /**
   * ratingId - a unique rating id used to report a rating
   */
  ratingId: number;
};

/**
 * The RatingCardActionsMenu component displays a menu with user actions
 *
 * actions available:
 * - report rating
 */
export const RatingCardActionsMenu = ({ ratingId, ...props }: RatingCardActionsMenuProps) => {
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();
  const {
    customerRatings: {
      api: { feedback },
    },
  } = useConfig();
  const [menuOpen, setMenuOpen] = useState(false);

  const actionHandler = async () => {
    try {
      const res = await fetch(feedback, {
        body: JSON.stringify({ feedback: 'notappropriate', id: ratingId }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error();
      }
      addNotification({
        severity: 'success',
        duration: 3000,
        message: formatMessage(ratingCardMessages.actionsMenuReportSuccess),
      });
    } catch (err) {
      logger.error(err, 'Error during giving report');
      addNotification({
        severity: 'error',
        duration: 3000,
        message: formatMessage(ratingCardMessages.actionsMenuReportError),
      });
    } finally {
      setMenuOpen(false);
    }
  };

  return (
    <div className="text-size-body2" {...props}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="icon-flat"
            aria-label={formatMessage(ratingCardMessages.actionsMenuAriaLabel)}
          >
            <span className="icon-emp-menu-android" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="px-0">
          <Button variant="icon-flat" className="w-auto rounded-none px-1" onClick={actionHandler}>
            {formatMessage(ratingCardMessages.actionsMenuReportText)}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
